import * as React from 'react'
import { CalendarBlank, Clock, Flame, Sparkle } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { useTruncation } from '../../lib/use-truncation'
import { Badge, type BadgeColor } from '../core/badge'
import { Button } from '../core/button'
import {
  Card,
  CardHeader,
  CardTitle,
  CardAction,
  CardContent,
  CardFooter,
} from '../core/card'
import { ExpandableText } from '../core/expandable-text'
import { entityTypeConfig, type EntityRef } from '../core/entity-glyph'
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '../core/tooltip'

/**
 * ActionCenterCard — one home Action Center recommendation as a card. The shared
 * unit behind the {@link ActionCenter} feed and the Card "Home Action Center"
 * stories, so the two can't drift.
 *
 *   <ActionCenterCard
 *     priority="hot"
 *     title="Advance Coastal Fresh, pilot wrapped"
 *     description="Amanda emailed yesterday: pilot wrapped…"
 *     entities={[
 *       { icon: <Handshake />, name: 'Coastal Fresh, Protein Line', href: '/deals/1' },
 *       { icon: <AddressBook />, name: 'Amanda Rivera' },  // no href → muted, not a link
 *     ]}
 *     onView={openPanel}
 *     onDismiss={dismiss}
 *   />
 *
 * `size` (`sm` | `md`) scales the whole card as a unit — Card padding + title (via
 * `Card size`), the body copy, the footer entity links, and the View / Dismiss
 * buttons all step down together. The AI mark + priority `Badge` stay at their
 * smallest in both. The footer stacks the linked records over a hairline with
 * **View / Dismiss anchored bottom-right**, in the same spot on every card no
 * matter how many entity links wrap above.
 */

/** How urgent an action is — drives its badge (and the feed's grouping + pills). */
export type ActionPriority = 'hot' | 'today' | 'thisWeek'

export type ActionCardSize = 'sm' | 'md'

/**
 * Priority → { label, color, icon }. The single place color is spent in the
 * Action Center — Hot/Today/This Week map to red/amber/blue so urgency reads at a
 * glance. Shared by the card badge, the feed's stat pills, and its group headers.
 */
export const PRIORITY_META: Record<
  ActionPriority,
  { label: string; color: BadgeColor; icon: React.ReactNode }
> = {
  hot: { label: 'Hot', color: 'error', icon: <Flame aria-hidden="true" /> },
  today: { label: 'Today', color: 'warning', icon: <Clock aria-hidden="true" /> },
  thisWeek: {
    label: 'This Week',
    color: 'blue',
    icon: <CalendarBlank aria-hidden="true" />,
  },
}

/** Fixed display order for priority groups + stat pills. */
export const PRIORITY_ORDER: ActionPriority[] = ['hot', 'today', 'thisWeek']

/**
 * AI mark — sized to match the adjacent priority Badge (20px tall, 12px glyph).
 * Regular-weight Sparkle to match the product's recommendation iconography
 * (fb-ui reserves `weight="fill"` for solid status/avatar glyphs).
 */
export const AiMark = () => (
  // Decorative: the sparkle repeats on every card and the "AI" framing is carried
  // by the surface (Action Center / Recently completed), so it's aria-hidden rather
  // than announcing "AI recommendation" on every row.
  <span
    aria-hidden="true"
    className="inline-flex size-5 items-center justify-center rounded-md bg-fbui-gray-100 text-fbui-gray-500"
  >
    <Sparkle className="size-3" />
  </span>
)

/**
 * A linked record (deal / company / contact) in the card footer: a muted leading
 * icon + the name. With `href` it's a brand-green link; without one it's plain
 * muted text (a record that doesn't navigate — e.g. a contact not yet in the CRM).
 * `size` scales the label + icon with the card. The label is capped at a max width
 * and truncates; when it's actually clipped, hovering (or focusing) reveals the
 * full name in a tooltip. Clipping is measured (via {@link useTruncation}), so
 * short names get no tooltip — and a non-link name becomes keyboard-focusable
 * *only* when clipped, so keyboard users can reveal it without adding a dead tab
 * stop to every short label.
 */
export const EntityLink = ({
  icon,
  href,
  size = 'md',
  children,
}: {
  icon?: React.ReactNode
  href?: string
  size?: ActionCardSize
  children: React.ReactNode
}) => {
  const [labelRef, truncated] = useTruncation<HTMLSpanElement>(children)

  const shared = cn(
    'inline-flex items-center gap-1.5 rounded outline-none [&>svg]:shrink-0 [&>svg]:text-fbui-gray-500',
    size === 'sm'
      ? 'max-w-[10rem] text-xs [&>svg]:size-3.5'
      : 'max-w-[14rem] text-sm [&>svg]:size-4'
  )
  const label = (
    <span ref={labelRef} className="min-w-0 truncate">
      {children}
    </span>
  )

  const trigger =
    href != null ? (
      <a
        href={href}
        className={cn(
          shared,
          'font-medium text-fbui-basil-700 transition-colors hover:text-fbui-basil-800 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand'
        )}
      >
        {icon}
        {label}
      </a>
    ) : (
      // Non-link: focusable only when clipped, so a keyboard user can focus it to
      // reveal the full name; otherwise it's plain, non-focusable muted text.
      <span
        className={cn(
          shared,
          'text-fbui-gray-500 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand'
        )}
        tabIndex={truncated ? 0 : undefined}
      >
        {icon}
        {label}
      </span>
    )

  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      {/* Only mount the tooltip when the name is actually clipped — otherwise
          hover/focus reveals nothing (there's nothing hidden to show). */}
      {truncated && <TooltipContent size="sm">{children}</TooltipContent>}
    </Tooltip>
  )
}

/**
 * The card footer's "linked records" row: the wrapping list of {@link EntityLink}s
 * (deal / company / contact …) shared by {@link ActionCenterCard} and the Completed
 * Actions card so the two can't drift on footer behavior. Clicks are stopped from
 * bubbling — the entity links are interactive and must not also trigger a clickable
 * card ancestor (the Action Center card opens on card-click).
 */
export const EntityLinkList = ({
  entities,
  size = 'md',
}: {
  entities: ActionCardEntity[]
  size?: ActionCardSize
}) => (
  <div
    className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1"
    onClick={(e) => e.stopPropagation()}
  >
    <TooltipProvider>
      {entities.map((entity, i) => (
        <EntityLink key={i} icon={entity.icon} href={entity.href} size={size}>
          {entity.name}
        </EntityLink>
      ))}
    </TooltipProvider>
  </div>
)

export interface ActionCardEntity {
  /** Leading glyph (a `@phosphor-icons/react` icon), rendered muted gray. */
  icon?: React.ReactNode
  /** Record name. */
  name: React.ReactNode
  /** Link target — renders the name as a brand link; omit for a non-navigable (muted) record. */
  href?: string
}

/**
 * Map a CRM {@link EntityRef} to the card's {@link ActionCardEntity} shape — the
 * glyph is resolved from the record's `type`. Shared by the Action Center feed and
 * the Completed Actions panel so the mapping can't drift.
 */
export const toCardEntity = (entity: EntityRef): ActionCardEntity => ({
  icon: entityTypeConfig[entity.type].icon,
  name: entity.name,
  href: entity.href,
})

export interface ActionCenterCardProps {
  /** The recommendation headline (the AI's action sentence). */
  title: React.ReactNode
  /** The AI "why" — clamped to two lines with a "Show more" toggle. */
  description?: React.ReactNode
  /** Urgency — sets the priority badge. */
  priority: ActionPriority
  /** Linked records shown in the footer (deal / company / contact …). */
  entities?: ActionCardEntity[]
  /** Show the AI "recommendation" mark. Default `true`. */
  ai?: boolean
  /** Scales the whole card as a unit. Default `md`. */
  size?: ActionCardSize
  onView?: () => void
  onDismiss?: () => void
  /** Primary action label. Default "View". */
  viewLabel?: string
  /** Secondary action label. Default "Dismiss". */
  dismissLabel?: string
  className?: string
}

export const ActionCenterCard = React.forwardRef<
  HTMLDivElement,
  ActionCenterCardProps
>(
  (
    {
      title,
      description,
      priority,
      entities,
      ai = true,
      size = 'md',
      onView,
      onDismiss,
      viewLabel = 'View',
      dismissLabel = 'Dismiss',
      className,
    },
    ref
  ) => {
    const meta = PRIORITY_META[priority]
    const buttonSize = size === 'sm' ? 'xs' : 'sm'
    // Description stays 14px even on the sm card — it's the primary rationale, so
    // readability wins over density.
    const bodyText = 'text-sm'
    const hasEntities = entities != null && entities.length > 0
    // The whole card opens the recommendation (redundant with the View button)
    // when `onView` is wired — a mouse convenience with a hover lift; the View
    // button stays the keyboard / assistive-tech path, so nothing is mouse-only.
    const interactive = onView != null
    return (
      <Card
        ref={ref}
        size={size}
        onClick={interactive ? onView : undefined}
        className={cn(
          interactive &&
            'cursor-pointer transition hover:border-fbui-gray-300 hover:shadow-fbui-md',
          className
        )}
      >
        {/* Tighter header→body gap than the Card default (its bottom padding). */}
        <CardHeader className={size === 'sm' ? 'pb-1.5' : 'pb-2'}>
          <CardTitle>{title}</CardTitle>
          <CardAction>
            <div className="flex items-center gap-2">
              {ai && <AiMark />}
              <Badge color={meta.color} prefixIcon={meta.icon}>
                {meta.label}
              </Badge>
            </div>
          </CardAction>
        </CardHeader>
        {description != null && (
          <CardContent>
            <ExpandableText
              lines={2}
              textClassName={cn(bodyText, 'text-fbui-gray-500')}
            >
              {description}
            </ExpandableText>
          </CardContent>
        )}
        {/* One bordered action bar — Card's own full-bleed top divider (via
            `bordered`) separates it from the body, then the linked records sit
            left and View / Dismiss right. `ml-auto` keeps the actions pinned
            right whether they share the row or wrap below a long entity list. */}
        <CardFooter bordered className="flex-wrap gap-x-4 gap-y-2">
          {hasEntities && <EntityLinkList entities={entities} size={size} />}
          <div className="ml-auto flex shrink-0 items-center gap-1">
            <Button
              size={buttonSize}
              onClick={(e) => {
                e.stopPropagation()
                onView?.()
              }}
            >
              {viewLabel}
            </Button>
            <Button
              variant="neutral"
              size={buttonSize}
              onClick={(e) => {
                e.stopPropagation()
                onDismiss?.()
              }}
            >
              {dismissLabel}
            </Button>
          </div>
        </CardFooter>
      </Card>
    )
  }
)
ActionCenterCard.displayName = 'ActionCenterCard'
